import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  borderRadius,
  borderStyle,
  borderWidth,
} from "../../../design-system/tokens/borders";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  fontSizes,
  fontWeights,
  lineHeights,
  typographyMixin,
} from "../../../design-system/tokens/typography";
import { shadows } from "../../../design-system/tokens/shadows";

/** Fixed card footprint from the design (F16): 128 × 72, 2px border. */
export const SLIDE_THUMBNAIL_WIDTH_PX = 128;
export const SLIDE_THUMBNAIL_HEIGHT_PX = 72;
const THUMBNAIL_BORDER_PX = 2;

/**
 * Inner preview viewport: the card minus its border on every side. Used to
 * compute the miniature scale factor without measuring the DOM (molecules are
 * stateless).
 */
export const SLIDE_THUMBNAIL_PREVIEW_WIDTH_PX =
  SLIDE_THUMBNAIL_WIDTH_PX - 2 * THUMBNAIL_BORDER_PX;
export const SLIDE_THUMBNAIL_PREVIEW_HEIGHT_PX =
  SLIDE_THUMBNAIL_HEIGHT_PX - 2 * THUMBNAIL_BORDER_PX;

export const Card = styled.button<{ $isActive?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${SLIDE_THUMBNAIL_WIDTH_PX}px;
  height: ${SLIDE_THUMBNAIL_HEIGHT_PX}px;
  padding: 0;
  overflow: hidden;
  background: ${rawColors.structural.white};
  border: ${borderWidth[2]} ${borderStyle.solid}
    ${({ $isActive }) =>
      $isActive ? rawColors.primary[500] : rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  cursor: pointer;

  &:hover {
    border-color: ${({ $isActive }) =>
      $isActive ? rawColors.primary[500] : rawColors.stroke[300]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadows.interactive.focus};
  }
`;

/** 10px bold label. Not shown on thumbnails (design has none); AddSlideCard reuses it. */
export const Title = styled.span`
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.bold};
  color: ${rawColors.text.mediumEmphasis};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PreviewArea = styled.span`
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 0;
`;

/**
 * Clips the scaled-down detail slide to the preview viewport. Decorative and
 * non-interactive — clicks must land on the thumbnail Card itself.
 */
export const MiniatureViewport = styled.span`
  position: relative;
  display: block;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  pointer-events: none;
`;

/**
 * Lays the real detail slide out at its natural size, then shrinks it with
 * transform: scale() so the thumbnail is an exact miniature of the detail
 * view (chosen over re-drawn previews so the two can never drift apart).
 */
export const MiniatureCanvas = styled.span<{
  $width: number;
  $height: number;
  $scale: number;
}>`
  position: absolute;
  top: 50%;
  left: 50%;
  display: block;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transform: translate(-50%, -50%) scale(${({ $scale }) => $scale});
`;

/** Rail item: number, framed thumbnail, name. The tint on the active item reaches the item's edge. */
export const RailItem = styled.div<{ $isActive?: boolean }>`
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[2]};
  border-radius: ${borderRadius[10]};
  background: ${({ $isActive }) =>
    $isActive ? rawColors.primary[100] : "transparent"};
`;

/** Order number: 600 11/16 on a 14px (0.875rem) column. */
export const RailNumber = styled.span`
  font-size: ${fontSizes["11"]};
  line-height: ${lineHeights["16"]};
  flex: none;
  width: 0.875rem;
  font-weight: ${fontWeights.semibold};
  color: ${rawColors.text.mediumEmphasis};
`;

/** The click target: frame plus name. */
export const RailButton = styled.button`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${shadows.interactive.focus};
    border-radius: ${borderRadius[5]};
  }
`;

export const RailFrame = styled.span`
  position: relative;
  display: flex;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  box-sizing: border-box;
  background: ${rawColors.structural.white};
  border: ${borderWidth[1]} ${borderStyle.solid} ${rawColors.stroke[200]};
  border-radius: ${borderRadius[5]};
`;

/** 500 11/16 name under the frame. */
export const RailName = styled.span`
  font-size: ${fontSizes["11"]};
  line-height: ${lineHeights["16"]};
  margin-top: ${spacing[1]};
  font-weight: ${fontWeights.medium};
  color: ${rawColors.text.mediumEmphasis};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RailActions = styled.span`
  position: absolute;
  top: ${spacing[2]};
  right: ${spacing[2]};
  display: inline-flex;
  opacity: 0;

  ${RailItem}:hover &,
  ${RailItem}:focus-within & {
    opacity: 1;
  }
`;
